import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';
import { Role } from 'constants/roles';

import { useMemo } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import omit from 'lodash/omit';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import {
  DateRangeTimezoneValue,
  DateRangeValue,
} from 'shared/mui/DatePicker/DateRangeTimezonePicker/DateRangeTimezonePicker';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { RequestsBankOpsListFilterChips } from './RequestsBankOpsListFilterChips';
import { RequestsBankOpsListFilterForm } from './RequestsBankOpsListFilterForm';

export type BankOperationsFilterValues = {
  appliedDate: DateRangeValue;
  createdDate: DateRangeValue;
  timezone: DateRangeTimezoneValue;
  merchant: string[];
  provider: string[];
  merchantAccount: string[];
  type: string[];
  direction: string;
  status: string[];
  referral: string[];
  user: string;
  requestView: string;
};

export const DEFAULT_VALUES = {
  appliedDate: [null, null] as DateRangeValue,
  createdDate: [null, null] as DateRangeValue,
  timezone: null,
  merchant: [],
  provider: [],
  merchantAccount: [],
  referral: [],
  type: [],
  status: [],
  direction: '',
  user: '',
};

export const RequestsBankOpsListFilter: React.FC = () => {
  const { filterValues } = useListContext();
  const { isHaveRole } = usePermissions();

  const isEmptyUrlFilter = Object.keys(cleanEmpty(filterValues)).length === 0;
  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<BankOperationsFilterValues>({
      defaultValues: {
        appliedDate: [
          formatInputToTimestamp(filterValues['dateOfOperation[gte]']),
          formatInputToTimestamp(filterValues['dateOfOperation[lte]']),
        ],
        createdDate: [
          formatInputToTimestamp(filterValues?.['createdAt[gte]']),
          formatInputToTimestamp(filterValues?.['createdAt[lte]']),
        ],
        timezone: filterValues?.timezone || null,
        merchant: filterValues.merchant || [],
        provider: filterValues.provider || [],
        merchantAccount: filterValues.merchantAccount || [],
        type: filterValues.type || [],
        status:
          filterValues.status ||
          (isEmptyUrlFilter && isHaveRole([Role.FinanceLead])
            ? [BankOperationStatusesNew.OnApproval]
            : []),
        referral: filterValues.referral || [],
        direction: filterValues.direction || '',
        user: filterValues.user || '',
        requestView: filterValues.requestView || '',
      },
      resetValues: DEFAULT_VALUES,
    });

  useSyncFilter<BankOperationsFilterValues>({
    appliedFilters,
    transform: (appliedFilters) => {
      const {
        appliedDate = [null, null],
        createdDate = [null, null],
        timezone,
        ...restFilters
      } = appliedFilters;
      return cleanEmpty({
        ...restFilters,
        'dateOfOperation[gte]': formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
        'dateOfOperation[lte]': formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        'createdAt[gte]': formatInputToTimestamp(createdDate?.[0], { unit: 'seconds' }),
        'createdAt[lte]': formatInputToTimestamp(createdDate?.[1], { unit: 'seconds' }),
        timezone: getAppliedTimezone(timezone, appliedDate, createdDate),
      });
    },
  });

  const { data } = useListContext();

  const listFilters = useMemo(() => {
    return omit(cleanEmpty(appliedFilters, false), 'timezone');
  }, [appliedFilters]);

  const isListFilterExist = Object.keys(listFilters).length > 0;

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton disabled={!data?.length} key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <RequestsBankOpsListFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <RequestsBankOpsListFilterChips />
            <FilterClearButton
              key="clear"
              onClick={() => {
                onReset();
              }}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
